/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 (original work) Open Assessment Technologies SA ;
 */

/**
 * "Max words limit" implementation for textarea.
 * Check if the wordcount of NEW (plain text) value exceeded maxWordsLimit, and fix it if necessary.
 * Will modify textarea value, and attempt to restore cursor position.
 * The way of modifying the value depends on the input method (UX reasons).
 * @param {InputEvent|CompositionEvent} e
 * @param {Object} options
 * @param {Number} options.maxWords - limit
 * @param {Number} options.wordCount - count for current value
 * @param {String} options.wordSeparators
 * @param {String} options.previousValue - previous value before input event, which is safe and doesn't exceed maxWords
 * @param {Number} options.lastCursorPosition - last cursor position for previous value
 *
 */
export function textareaConstrainMaxWords(
    e,
    { maxWords, wordCount, wordSeparators, previousValue, lastCursorPosition } = {}
) {
    if (maxWords) {
        const element = e.target;
        const currentValue = element.value;
        let newValue = currentValue;

        const isInputEvent = e instanceof InputEvent;
        const isComposing = e instanceof InputEvent && e.isComposing;
        if (!isComposing) {
            if (wordCount > maxWords) {
                if (isInputEvent && e.inputType.includes('insertFromPaste') && !previousValue.trim().length) {
                    // clean paste: truncate at end
                    newValue = truncateWords(currentValue, maxWords, wordSeparators);
                    element.value = newValue;
                } else {
                    // revert changed value
                    newValue = previousValue;
                    element.value = newValue; // losing Ctrl+Z/Y history
                    element.setSelectionRange(lastCursorPosition, lastCursorPosition);
                }
            }
        }
    }
}

/**
 * Truncate a (plain text) value to the permitted number of words
 * @param {string} text
 * @param {number} maxWords
 * @param {string} wordSeparators
 * @returns {string} truncated text
 */
function truncateWords(text, maxWords, wordSeparators) {
    const separators = wordSeparators || '\\s';
    const wordsAndSeps = text.split(new RegExp(`([${separators}]+)`)); // [word, seps, word, seps, ...]
    const truncatedWordsAndSeps = wordsAndSeps.slice(0, maxWords * 2);
    return truncatedWordsAndSeps.join('');
}

/**
 * Custom `maxlength` attribute implementation for input & textarea,
 * to use if browser doesn't support it natively.
 * It will cut input/textarea value, and attempt to restore cursor position (but Ctrl+Z/Y won't work well).
 *
 * note: composition events are fired for language keyboards with autocomplete (japanese);
 * native browser implementation allows to
 * enter more than maxlength while choosing autocomplete value (`input` event with `isComposing: true`),
 * and cuts it only when finished choosing (`compositionend` event)
 * @param {InputEvent|CompositionEvent} e
 * @param {Number} maxlength
 */
export function applyInputTextareaMaxlength(e, maxlength) {
    if (maxlength) {
        const element = e.target;
        let value = element.value;
        const isComposing = e instanceof InputEvent && e.isComposing;
        if (!isComposing) {
            let adjusted = false;
            let adjustedCaretOffset = 0;
            if (value && value.length > maxlength) {
                let cutEndOffset = element.selectionEnd;
                let cutStartOffset = cutEndOffset - (value.length - maxlength);
                if (cutStartOffset < 0) {
                    cutEndOffset = cutEndOffset - cutStartOffset;
                    cutStartOffset = 0;
                }
                value = value.substring(0, cutStartOffset) + value.substring(cutEndOffset);
                adjustedCaretOffset = Math.min(cutStartOffset, value.length);
                adjusted = true;
            }

            if (adjusted) {
                element.value = value; // losing Ctrl+Z/Y history
                element.selectionEnd = adjustedCaretOffset;
                element.selectionStart = adjustedCaretOffset;
            }
        }
    }
}

/**
 * To make 'contenteditable="plaintext-only"' behave like native Input,
 * modify its value accordingly:
 * - ensure there's only plain text
 * - do not allow linebreaks
 * - [optional] do not exceed maxlength
 * It will update contenteditable value, and attempt to restore cursor position.
 * @param {InputEvent|CompositionEvent} e
 * @param {Object} options
 * @param {Number?} options.maxlength
 */
export function applyContenteditableRestrictions(e, { maxlength } = {}) {
    const element = e.target;
    const isComposing = e instanceof InputEvent && e.isComposing;
    if (!isComposing) {
        contenteditableEnsurePlainText(element);
        contenteditableEnsureNoLinebreak(element);
        contenteditableEnsureMaxlength(element, maxlength);
    }
}

function contenteditableEnsurePlainText(element) {
    const nonTextChildNodes = [...element.childNodes].filter(childNode => childNode.nodeType !== Node.TEXT_NODE);
    nonTextChildNodes.forEach(childNode => {
        if (childNode.textContent?.length) {
            //should never happen with "contenteditable='plaintext-only'"
            childNode.replaceWith(childNode.textContent);
        } else {
            //if you add text, then remove it, there's <br> inside
            childNode.remove(); //Ctrl+Z/Ctrl+Y becomes unnatural
        }
    });
}

function contenteditableEnsureNoLinebreak(element) {
    let val = element.textContent;
    if (val.includes('\n') || val.includes('\r')) {
        const valWithoutLinebreak = val.replace(/\r/g, '').replace(/\n/g, '');
        element.textContent = valWithoutLinebreak; //Ctrl+Z/Ctrl+Y becomes unnatural
        //move caret to the end; ignore restoring position - it's the case for paste/drop only
        contenteditableSetCursor(element.lastChild, element.lastChild.length);
    }
}

function contenteditableEnsureMaxlength(element, maxlength) {
    const documentSelection = window.getSelection();
    let val = element.textContent;
    if (maxlength && val && val.length > maxlength) {
        const childNodes = [...element.childNodes];
        let focusNode;
        let focusOffset;
        if (childNodes.includes(documentSelection.focusNode)) {
            focusNode = documentSelection.focusNode;
            focusOffset = documentSelection.focusOffset;
        } else {
            focusNode = childNodes[childNodes.length - 1];
            focusOffset = focusNode.nodeValue.length;
        }

        const offsets = [];
        let totalOffset = 0;
        for (let i = 0; i < childNodes.length; i++) {
            offsets[i] = totalOffset;
            totalOffset += childNodes[i].nodeValue.length;
        }

        const lengthToCut = val.length - maxlength;
        let cutEndOffset = offsets[childNodes.indexOf(focusNode)] + focusOffset;
        let cutStartOffset = cutEndOffset - lengthToCut;
        if (cutStartOffset < 0) {
            cutEndOffset = cutEndOffset - cutStartOffset;
            cutStartOffset = 0;
        }

        //cut; Ctrl+Z/Ctrl+Y becomes unnatural
        element.normalize();
        const cutRange = document.createRange();
        cutRange.setStart(element.firstChild, cutStartOffset);
        cutRange.setEnd(element.firstChild, cutEndOffset);
        cutRange.deleteContents();

        //try to restore caret position
        documentSelection.removeAllRanges();
        documentSelection.addRange(cutRange);
    }
}

function contenteditableSetCursor(childNode, offset) {
    const endRange = document.createRange();
    endRange.setStart(childNode, offset);
    endRange.setEnd(childNode, offset);

    const documentSelection = window.getSelection();
    documentSelection.removeAllRanges();
    documentSelection.addRange(endRange);
}
